import { useEffect, useRef, useState, useCallback } from 'react';

export interface UseInactivityTimerOptions {
    totalTimeout: number;        // Tempo total em ms (300000 = 5min)
    warningTimeout: number;      // Quando mostrar aviso em ms (60000 = 1min)
    onWarning: () => void;       // Callback quando aviso aparece
    onTimeout: () => void;       // Callback quando tempo esgota
    events?: string[];           // Eventos a monitorar (default: ['mousemove'])
}

export interface UseInactivityTimerReturn {
    remainingTime: number;       // Segundos restantes
    isWarningActive: boolean;    // Se está mostrando aviso
    resetTimer: () => void;      // Função para resetar manualmente
}

export const useInactivityTimer = (options: UseInactivityTimerOptions): UseInactivityTimerReturn => {
    const {
        totalTimeout,
        warningTimeout,
        onWarning,
        onTimeout,
        events = ['mousemove']
    } = options;

    const [remainingTime, setRemainingTime] = useState<number>(0);
    const [isWarningActive, setIsWarningActive] = useState<boolean>(false);

    const mainTimerRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isWarningActiveRef = useRef<boolean>(false);

    // Limpa todos os timers
    const clearAllTimers = () => {
        if (mainTimerRef.current) {
            clearTimeout(mainTimerRef.current);
            mainTimerRef.current = null;
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    };

    // Inicia o countdown de segundos
    const startCountdown = () => {
        const warningTimeoutSeconds = Math.floor(warningTimeout / 1000);
        setRemainingTime(warningTimeoutSeconds);

        countdownIntervalRef.current = setInterval(() => {
            setRemainingTime((prev) => {
                if (prev <= 1) {
                    if (countdownIntervalRef.current) {
                        clearInterval(countdownIntervalRef.current);
                        countdownIntervalRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Reseta o timer completamente
    const resetTimer = useCallback(() => {
        clearAllTimers();
        setIsWarningActive(false);
        isWarningActiveRef.current = false;
        setRemainingTime(0);

        // Inicia o timer principal (até o aviso)
        const timeUntilWarning = totalTimeout - warningTimeout;
        mainTimerRef.current = setTimeout(() => {
            setIsWarningActive(true);
            isWarningActiveRef.current = true;
            onWarning();
            startCountdown();

            // Timer de aviso (até o logout)
            warningTimerRef.current = setTimeout(() => {
                onTimeout();
            }, warningTimeout);
        }, timeUntilWarning);
    }, [totalTimeout, warningTimeout, onWarning, onTimeout]);

    // Handler de eventos de atividade
    const handleActivity = useCallback(() => {
        // Só reseta se não estiver no modo de aviso
        if (!isWarningActiveRef.current) {
            resetTimer();
        }
    }, [resetTimer]);

    // Configura event listeners e inicia timer pela primeira vez
    useEffect(() => {
        // Inicia o timer pela primeira vez
        resetTimer();

        // Adiciona event listeners
        events.forEach((event) => {
            window.addEventListener(event, handleActivity);
        });

        // Cleanup
        return () => {
            clearAllTimers();
            events.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        remainingTime,
        isWarningActive,
        resetTimer
    };
};

export default useInactivityTimer;
